/*
__/\\\\\\\\\\\\\\\__/\\\\\\\\\\\\\\\_____/\\\\\\\\\____        
 _\///////\\\/////__\///////\\\/////____/\\\\\\\\\\\\\__       
  _______\/\\\_____________\/\\\________/\\\/////////\\\_      
   _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_     
    _______\/\\\_____________\/\\\_______\/\\\\\\\\\\\\\\\_    
     _______\/\\\_____________\/\\\_______\/\\\/////////\\\_   
      _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_  
       _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_ 
        _______\///______________\///________\///________\///__
            
            COPYRIGHT TACTICAL TRANSPORTATION ADVISORS, INC. 
            ALL RIGHTS RESERVED.
*/

import React, { useEffect, useRef, useState } from "react";
import { Dropdown, Form, InputGroup, Modal, Pagination } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import QuickTable from "../../components/QuickTable";
import CustomButton from "../../components/CustomButton";
import PageSpinner from "../../components/PageSpinner";
import DocumentRequestDetails from "./DocumentRequestDetails";
import { getHistorical } from "../../services/DashboardService";
import { downloadDocumentRequest } from "../../services/HRDocumentsService";
import { DirectDepositDocuments, OnboardingDocumentsReadOnly, reactPdfToBase64, taxDocumentDictionary } from "../../tools";
import PDFMerger from "pdf-merger-js/browser";
import { Document, Image, Page, Text } from "@react-pdf/renderer";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function DashboardOldHistorical() {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [filterType, setFilterType] = useState("Company");
  const [filterValue, setFilterValue] = useState("");
  const [debouncedFilterValue, setDebouncedFilterValue] = useState("");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const loadData = async (pg, ps, filters) => {
    setIsLoading(true);

    const response = await getHistorical({
      page: pg,
      pageSize: ps,
      ...filters,
    });

    if (response?.status === "200") {
      setRows(response.rows ?? []);
      setTotalCount(response.totalCount ?? 0);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedFilterValue(filterValue);
    }, 500);

    return () => clearTimeout(timeout);
  }, [filterValue]);

  useEffect(() => {
    loadData(page, pageSize, {
      filterType,
      filterValue: debouncedFilterValue,
      dateFrom,
      dateTo,
    });
  }, [page, pageSize, filterType, debouncedFilterValue, dateFrom, dateTo]);

  const handleClearFilters = () => {
    setPage(1);
    setFilterType("Company");
    setFilterValue("");
    setDateFrom("");
    setDateTo("");
  };

  const filterTypeItems = ["Company", "CSA/Terminal", "Employee"].map((type) => (
    <Dropdown.Item
      key={type}
      active={filterType === type}
      onClick={() => {
        setPage(1);
        setFilterType(type);
      }}
    >
      {type}
    </Dropdown.Item>
  ));

  const pageSizeItems = PAGE_SIZE_OPTIONS.map((size) => (
    <Dropdown.Item
      key={size}
      active={pageSize === size}
      onClick={() => {
        setPageSize(size);
        setPage(1);
      }}
    >
      {size} per page
    </Dropdown.Item>
  ));

  const tableRows = rows.map((request) => (
    <HistoricalRow
      key={request.uid}
      request={request}
      onSelect={() => {
        setSelectedRequest(request);
        setShowModal(true);
      }}
    />
  ));

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 12,
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <InputGroup style={{ flex: "1 1 280px", minWidth: 280 }}>
          <InputGroup.Text>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </InputGroup.Text>

          <Form.Control
            placeholder={`Filter by ${filterType}`}
            autoComplete="off"
            value={filterValue}
            onChange={(e) => {
              setPage(1);
              setFilterValue(e.target.value);
            }}
          />

          <Dropdown>
            <Dropdown.Toggle>{filterType}</Dropdown.Toggle>

            <Dropdown.Menu>{filterTypeItems}</Dropdown.Menu>
          </Dropdown>
        </InputGroup>

        <div style={{ display: "flex", gap: 8 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <Form.Label style={{ margin: 0, fontSize: 14 }}>From</Form.Label>

            <Form.Control
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setPage(1);
                setDateFrom(e.target.value);
              }}
              style={{ width: 150 }}
            />
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <Form.Label style={{ margin: 0, fontSize: 14 }}>To</Form.Label>

            <Form.Control
              type="date"
              value={dateTo}
              onChange={(e) => {
                setPage(1);
                setDateTo(e.target.value);
              }}
              style={{ width: 150 }}
            />
          </div>
        </div>

        <CustomButton label="Clear" onClick={handleClearFilters} style={{ backgroundColor: "#6c757d" }} />
      </div>

      {isLoading ? (
        <PageSpinner />
      ) : (
        <>
          <QuickTable
            responsive={false}
            title={`Historical Document Requests (${totalCount})`}
            headers={["Company", "Location", "State", "Employee", "Date Created", "Date Dismissed", "Documents Requested", "Status", ""]}
            rows={tableRows}
            widths={[null, null, null, null, 1, null, null, 1, 1]}
            noWrap
            size="sm"
          />

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: 12,
              flexWrap: "wrap",
              gap: 12,
              width: "100%",
            }}
          >
            <span style={{ fontSize: 13, color: "#6c757d", whiteSpace: "nowrap" }}>
              {totalCount === 0 ? "0 results" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} of ${totalCount} results`}
            </span>

            <Pagination style={{ margin: 0 }}>
              <Pagination.First disabled={page === 1} onClick={() => setPage(1)} />

              <Pagination.Prev disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => Math.abs(p - page) <= 2 || p === 1 || p === totalPages)
                .map((p, i, arr) => (
                  <React.Fragment key={p}>
                    {i > 0 && arr[i - 1] + 1 !== p && <Pagination.Ellipsis disabled />}

                    <Pagination.Item active={p === page} onClick={() => setPage(p)}>
                      {p}
                    </Pagination.Item>
                  </React.Fragment>
                ))}

              <Pagination.Next disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />

              <Pagination.Last disabled={page === totalPages} onClick={() => setPage(totalPages)} />
            </Pagination>

            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" size="sm">
                {pageSize} per page
              </Dropdown.Toggle>

              <Dropdown.Menu>{pageSizeItems}</Dropdown.Menu>
            </Dropdown>
          </div>
        </>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
        {selectedRequest && <DocumentRequestDetails documentRequest={selectedRequest} handleRemoveEdDocument={() => {}} handleSetEDDocumentIncomplete={() => {}} />}
      </Modal>
    </div>
  );
}

function HistoricalRow({ request, onSelect }) {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (e) => {
    e.stopPropagation();
    setIsDownloading(true);
    const response = await downloadDocumentRequest(request.uid);
    if (response.status == "200") {
      const merger = new PDFMerger();
      const sortedDocumentTypes = ["offerLetter", "i9", "dl", "ssn", "w4", ...Object.keys(taxDocumentDictionary), ...Object.keys(DirectDepositDocuments), "f8850"].filter((d) =>
        Object.keys(response.documents).includes(d),
      );

      for (let i = 0; i < sortedDocumentTypes.length; i++) {
        const doc = response.documents[sortedDocumentTypes[i]];
        if (doc?.base64) {
          if (!doc.base64.split(",", 1)[0].includes("pdf")) {
            const pdf = (
              <Document>
                <Page size="LETTER" style={{ position: "relative", padding: 50 }}>
                  <Text style={{ fontSize: 15, textAlign: "center", marginBottom: 12 }}>
                    {sortedDocumentTypes[i] === "dl" ? "Driver's License" : sortedDocumentTypes[i] === "ssn" ? "Social Security Card" : ""}
                  </Text>
                  <Image style={{ objectFit: "contain", maxHeight: 600 }} src={doc.base64} />
                </Page>
              </Document>
            );
            const base64 = await reactPdfToBase64(pdf);
            await merger.add(await (await fetch(base64)).blob());
          } else {
            await merger.add(await (await fetch(doc.base64)).blob());
          }
        }
      }
      for (const customDoc of response.documents.custom ?? []) {
        if (customDoc.base64) {
          await merger.add(await (await fetch(customDoc.base64)).blob(), null);
        }
      }
      const mergedPdf = await merger.saveAsBlob();
      const elem = window.document.createElement("a");
      elem.target = "_blank";
      elem.href = window.URL.createObjectURL(mergedPdf);
      elem.download = `${request.employee?.firstName ?? ""} ${request.employee?.lastName ?? ""} Document Request ${moment(request.dateCreated).format("MMM D, YYYY")}`;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    }
    setIsDownloading(false);
  };

  const includedDocuments = Object.keys(request.documents ?? {});
  let documents = ["offerLetter", "i9", "w4", ...Object.keys(taxDocumentDictionary), ...Object.keys(DirectDepositDocuments), "f8850"]
    .filter((d) => includedDocuments.includes(d))
    .map((d) => OnboardingDocumentsReadOnly[d]);

  if (request.documents?.i9) {
    documents.push("Driver's License");
    documents.push("Social Security Card");
  }
  if (request.documents?.custom?.length > 0) {
    documents.push(...request.documents.custom.map((doc) => doc.documentInfo?.name));
  }

  return (
    <tr className="cursor-pointer" onClick={onSelect}>
      <td>{request.employee?.companyName}</td>
      <td>{request.employee?.csa?.csaName ?? request.employee?.terminalName ?? request.employee?.locationName}</td>
      <td>{request.employee?.state}</td>
      <td>
        {request.employee?.firstName} {request.employee?.lastName}
      </td>
      <td>{moment(request.dateCreated).format("MMM D, YYYY")}</td>
      <td>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>{moment(request.adminDateDismissed).format("MMM D, YYYY")}</span>
          <span style={{ color: "#6c757d", fontSize: "0.85em" }}>{moment(request.adminDateDismissed).format("h:mm A")}</span>
        </div>
      </td>
      <td>{documents.join(", ")}</td>
      <td>
        {request.employerCompleted ? (
          <span style={{ color: "#198754", fontWeight: 500, whiteSpace: "nowrap" }}>Employer Completed</span>
        ) : (
          <span style={{ color: "#fd7e14", fontWeight: 500, whiteSpace: "nowrap" }}>Awaiting Employer</span>
        )}
      </td>
      <td>
        <CustomButton label="Download All" style={{ padding: "0px 6px", whiteSpace: "nowrap" }} isLoading={isDownloading} onClick={handleDownload} />
      </td>
    </tr>
  );
}
